/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useRef, useCallback, useMemo, useState, useEffect } from "react";
import ForceGraph3D from "react-force-graph-3d";
import * as THREE from "three";
import { getCausalChain } from "../../api/api";
import { formatStoredDate } from "../../utils/datetime";

// == Constants ==
const stripHtml = (html) => {
  if (!html) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const EVENT_TYPE_COLORS = {
  MACRO_RELEASE: "#3b82f6",
  CENTRAL_BANK: "#8b5cf6",
  PRICE_MOVE: "#f59e0b",
  POSITIONING: "#10b981",
  NEWS: "#6366f1",
  GEOPOLITICAL: "#ef4444",
  SENTIMENT_SHIFT: "#ec4899",
  INSIDER_TRADING: "#f97316",
};

const EDGE_TYPE_COLORS = {
  TRIGGERED: "#f59e0b",
  REINFORCED: "#10b981",
  CONTRADICTED: "#8b5cf6",
  AMPLIFIED: "#3b82f6",
  DAMPENED: "#ec4899",
  CAUSED: "#ef4444",
  PRECEDED: "#6b7280",
  FOLLOWED_BY: "#6b7280",
};

const EDGE_TYPE_LABELS = {
  TRIGGERED: "Triggered",
  REINFORCED: "Reinforced",
  CONTRADICTED: "Contradicted",
  AMPLIFIED: "Amplified",
  DAMPENED: "Dampened",
  CAUSED: "Caused",
  PRECEDED: "Preceded",
  FOLLOWED_BY: "Followed By",
};

const EVENT_TYPE_LABELS = {
  MACRO_RELEASE: "Macro Release",
  CENTRAL_BANK: "Central Bank",
  PRICE_MOVE: "Price Move",
  POSITIONING: "Positioning",
  NEWS: "News",
  GEOPOLITICAL: "Geopolitical",
  SENTIMENT_SHIFT: "Sentiment Shift",
  INSIDER_TRADING: "Insider Trading",
};

const DETAILED_RENDER_NODE_LIMIT = 500;
const SIMULATED_LAYOUT_NODE_LIMIT = 1200;
const ARROW_RENDER_NODE_LIMIT = 2500;
const MASSIVE_GRAPH_NODE_LIMIT = 5000;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

const getEndpointId = (endpoint) => String(
  endpoint && typeof endpoint === "object" ? endpoint.id : endpoint
);

const getLinkSourceId = (link) => String(link.sourceId ?? getEndpointId(link.source));
const getLinkTargetId = (link) => String(link.targetId ?? getEndpointId(link.target));
const getNodeId = (node) => String(node?.id ?? "");

const hashString = (input) => {
  const text = String(input ?? "");
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const hashUnit = (input) => hashString(input) / 4294967295;

const getGraphScale = (nodeCount) => {
  if (nodeCount > 10000) return 2700;
  if (nodeCount > MASSIVE_GRAPH_NODE_LIMIT) return 2300;
  if (nodeCount > ARROW_RENDER_NODE_LIMIT) return 1850;
  if (nodeCount > SIMULATED_LAYOUT_NODE_LIMIT) return 1450;
  if (nodeCount > DETAILED_RENDER_NODE_LIMIT) return 900;
  return 430;
};

const getSpherePoint = (index, count, radius, phase = 0) => {
  if (count <= 1) return { x: 0, y: 0, z: 0 };
  const yNorm = 1 - (2 * index) / (count - 1);
  const ringRadius = Math.sqrt(Math.max(0, 1 - yNorm * yNorm));
  const theta = index * GOLDEN_ANGLE + phase;
  return {
    x: Math.cos(theta) * ringRadius * radius,
    y: yNorm * radius,
    z: Math.sin(theta) * ringRadius * radius,
  };
};

const getFilledSpherePoint = (index, count, radius, phase = 0) => {
  if (count <= 1 || index === 0) return { x: 0, y: 0, z: 0 };
  const shellPoint = getSpherePoint(index - 1, Math.max(count - 1, 1), 1, phase);
  const fill = Math.cbrt(index / Math.max(count - 1, 1));
  const deterministicJitter = 0.88 + hashUnit(`fill:${index}:${count}:${phase}`) * 0.18;
  const r = radius * fill * deterministicJitter;
  return {
    x: shellPoint.x * r,
    y: shellPoint.y * r,
    z: shellPoint.z * r,
  };
};

const computeRelationshipClusters = (nodes, links) => {
  const nodeIds = nodes.map((node) => node.id);
  const nodeIdSet = new Set(nodeIds);
  const adjacency = new Map(nodeIds.map((id) => [id, []]));
  const degree = new Map(nodeIds.map((id) => [id, 0]));
  const edgeTypeWeights = {
    CAUSED: 1.45,
    TRIGGERED: 1.3,
    REINFORCED: 1.15,
    AMPLIFIED: 1.1,
    CONTRADICTED: 1,
    DAMPENED: 1,
    PRECEDED: 0.8,
    FOLLOWED_BY: 0.8,
  };

  links.forEach((link) => {
    const source = getLinkSourceId(link);
    const target = getLinkTargetId(link);
    if (!nodeIdSet.has(source) || !nodeIdSet.has(target) || source === target) return;
    const confidence = Number(link.confidence ?? 0.55);
    const weight = Math.max(0.2, confidence) * (edgeTypeWeights[link.edgeType] || 1);
    adjacency.get(source).push({ id: target, weight });
    adjacency.get(target).push({ id: source, weight });
    degree.set(source, (degree.get(source) || 0) + weight);
    degree.set(target, (degree.get(target) || 0) + weight);
  });

  if (!links.length) {
    const byType = new Map();
    const clusterById = new Map();
    nodes.forEach((node) => {
      const key = node.eventType || "UNKNOWN";
      if (!byType.has(key)) byType.set(key, byType.size);
      clusterById.set(node.id, byType.get(key));
    });
    const clusterSizes = Array.from({ length: byType.size }, () => 0);
    clusterById.forEach((clusterId) => { clusterSizes[clusterId] += 1; });
    return { clusterById, clusterSizes };
  }

  const visited = new Set();
  const components = [];
  nodeIds
    .filter((id) => (adjacency.get(id) || []).length > 0)
    .sort((a, b) => hashString(a) - hashString(b))
    .forEach((startId) => {
      if (visited.has(startId)) return;
      const component = [];
      const stack = [startId];
      visited.add(startId);
      while (stack.length) {
        const current = stack.pop();
        component.push(current);
        (adjacency.get(current) || []).forEach((neighbor) => {
          if (visited.has(neighbor.id)) return;
          visited.add(neighbor.id);
          stack.push(neighbor.id);
        });
      }
      components.push(component);
    });

  const rawClusterById = new Map();
  let nextClusterId = 0;

  components
    .sort((a, b) => b.length - a.length || hashString(a[0]) - hashString(b[0]))
    .forEach((component) => {
      const targetCount = component.length < 90
        ? 1
        : Math.min(30, Math.max(2, Math.ceil(component.length / 260)));
      const sortedByDegree = [...component].sort((a, b) => {
        const degreeDiff = (degree.get(b) || 0) - (degree.get(a) || 0);
        return degreeDiff || hashString(a) - hashString(b);
      });

      if (targetCount === 1) {
        const clusterId = nextClusterId;
        nextClusterId += 1;
        component.forEach((id) => rawClusterById.set(id, clusterId));
        return;
      }

      const seeds = [];
      const usedSeedNeighborhood = new Set();
      sortedByDegree.forEach((id) => {
        if (seeds.length >= targetCount) return;
        const neighborhoodKey = (adjacency.get(id) || [])
          .slice()
          .sort((a, b) => b.weight - a.weight)
          .slice(0, 4)
          .map((neighbor) => neighbor.id)
          .sort()
          .join("|");
        if (usedSeedNeighborhood.has(neighborhoodKey) && seeds.length < targetCount - 1) return;
        usedSeedNeighborhood.add(neighborhoodKey);
        seeds.push(id);
      });
      sortedByDegree.some((id) => {
        if (seeds.length >= targetCount) return true;
        if (!seeds.includes(id)) seeds.push(id);
        return false;
      });

      const seedSet = new Set(seeds);
      const queue = [];
      seeds.forEach((seedId) => {
        const clusterId = nextClusterId;
        nextClusterId += 1;
        rawClusterById.set(seedId, clusterId);
        queue.push(seedId);
      });

      for (let cursor = 0; cursor < queue.length; cursor += 1) {
        const current = queue[cursor];
        const clusterId = rawClusterById.get(current);
        const neighbours = (adjacency.get(current) || []).slice().sort((a, b) => b.weight - a.weight);
        neighbours.forEach((neighbor) => {
          if (rawClusterById.has(neighbor.id)) return;
          rawClusterById.set(neighbor.id, clusterId);
          queue.push(neighbor.id);
        });
      }

      component.forEach((id) => {
        if (!rawClusterById.has(id)) {
          rawClusterById.set(id, rawClusterById.get(seeds[hashString(id) % seeds.length]));
        }
      });

      for (let iteration = 0; iteration < 4; iteration += 1) {
        [...component]
          .sort((a, b) => hashString(`${iteration}:${a}`) - hashString(`${iteration}:${b}`))
          .forEach((id) => {
            if (seedSet.has(id)) return;
            const scores = new Map();
            (adjacency.get(id) || []).forEach((neighbor) => {
              const clusterId = rawClusterById.get(neighbor.id);
              if (clusterId == null) return;
              scores.set(clusterId, (scores.get(clusterId) || 0) + neighbor.weight);
            });
            if (!scores.size) return;
            const currentCluster = rawClusterById.get(id);
            const currentScore = scores.get(currentCluster) || 0;
            const [bestCluster, bestScore] = [...scores.entries()].sort(([aId, aScore], [bId, bScore]) => (
              bScore - aScore || aId - bId
            ))[0];
            if (bestScore > currentScore * 1.08) rawClusterById.set(id, bestCluster);
          });
      }
    });

  nodes.forEach((node) => {
    if (!rawClusterById.has(node.id)) {
      rawClusterById.set(node.id, nextClusterId);
      nextClusterId += 1;
    }
  });

  const rawCounts = new Map();
  rawClusterById.forEach((clusterId) => {
    rawCounts.set(clusterId, (rawCounts.get(clusterId) || 0) + 1);
  });
  const compactIds = [...rawCounts.entries()]
    .sort(([, a], [, b]) => b - a)
    .map(([clusterId]) => clusterId);
  const compactIndex = new Map(compactIds.map((clusterId, index) => [clusterId, index]));
  const compactClusterById = new Map();
  const clusterSizes = Array.from({ length: compactIds.length }, () => 0);

  rawClusterById.forEach((clusterId, nodeId) => {
    const compactClusterId = compactIndex.get(clusterId) ?? 0;
    compactClusterById.set(nodeId, compactClusterId);
    clusterSizes[compactClusterId] += 1;
  });

  return { clusterById: compactClusterById, clusterSizes };
};

const computeClusterCenters = (clusterCount, clusterSizes, links, clusterById, radius) => {
  if (clusterCount <= 1) return [{ x: 0, y: 0, z: 0 }];

  const centers = Array.from({ length: clusterCount }, (_, index) => (
    getSpherePoint(index, clusterCount, radius * 0.72, hashUnit(`cluster-center:${index}`) * Math.PI * 2)
  ));
  const velocities = Array.from({ length: clusterCount }, () => ({ x: 0, y: 0, z: 0 }));
  const linkWeights = new Map();

  links.forEach((link) => {
    const sourceCluster = clusterById.get(getLinkSourceId(link));
    const targetCluster = clusterById.get(getLinkTargetId(link));
    if (sourceCluster == null || targetCluster == null || sourceCluster === targetCluster) return;
    const key = sourceCluster < targetCluster
      ? `${sourceCluster}|${targetCluster}`
      : `${targetCluster}|${sourceCluster}`;
    linkWeights.set(key, (linkWeights.get(key) || 0) + Math.max(0.2, Number(link.confidence ?? 0.55)));
  });

  const clusterLinks = [...linkWeights.entries()].map(([key, weight]) => {
    const [source, target] = key.split("|").map(Number);
    return { source, target, weight };
  });
  const targetDistance = Math.max(82, Math.min(230, radius / Math.max(2.5, Math.cbrt(clusterCount) * 1.2)));

  for (let iteration = 0; iteration < 150; iteration += 1) {
    const forces = Array.from({ length: clusterCount }, () => ({ x: 0, y: 0, z: 0 }));

    for (let i = 0; i < clusterCount; i += 1) {
      for (let j = i + 1; j < clusterCount; j += 1) {
        const dx = centers[i].x - centers[j].x;
        const dy = centers[i].y - centers[j].y;
        const dz = centers[i].z - centers[j].z;
        const distance = Math.max(16, Math.hypot(dx, dy, dz));
        const sizeBias = Math.sqrt((clusterSizes[i] || 1) * (clusterSizes[j] || 1));
        const strength = (radius * 8.5 + sizeBias * 0.18) / (distance * distance);
        const fx = (dx / distance) * strength;
        const fy = (dy / distance) * strength;
        const fz = (dz / distance) * strength;
        forces[i].x += fx;
        forces[i].y += fy;
        forces[i].z += fz;
        forces[j].x -= fx;
        forces[j].y -= fy;
        forces[j].z -= fz;
      }
    }

    clusterLinks.forEach(({ source, target, weight }) => {
      const dx = centers[target].x - centers[source].x;
      const dy = centers[target].y - centers[source].y;
      const dz = centers[target].z - centers[source].z;
      const distance = Math.max(12, Math.hypot(dx, dy, dz));
      const strength = Math.min(0.034, 0.005 + Math.log1p(weight) * 0.0035);
      const pull = (distance - targetDistance) * strength;
      const fx = (dx / distance) * pull;
      const fy = (dy / distance) * pull;
      const fz = (dz / distance) * pull;
      forces[source].x += fx;
      forces[source].y += fy;
      forces[source].z += fz;
      forces[target].x -= fx;
      forces[target].y -= fy;
      forces[target].z -= fz;
    });

    for (let i = 0; i < clusterCount; i += 1) {
      forces[i].x -= centers[i].x * 0.004;
      forces[i].y -= centers[i].y * 0.004;
      forces[i].z -= centers[i].z * 0.004;
      velocities[i].x = (velocities[i].x + forces[i].x) * 0.68;
      velocities[i].y = (velocities[i].y + forces[i].y) * 0.68;
      velocities[i].z = (velocities[i].z + forces[i].z) * 0.68;
      centers[i].x += velocities[i].x;
      centers[i].y += velocities[i].y;
      centers[i].z += velocities[i].z;
    }
  }

  const maxDistance = centers.reduce((max, center) => (
    Math.max(max, Math.hypot(center.x, center.y, center.z))
  ), 1);
  const maxRadius = radius * 0.9;
  if (maxDistance > maxRadius) {
    const scale = maxRadius / maxDistance;
    centers.forEach((center) => {
      center.x *= scale;
      center.y *= scale;
      center.z *= scale;
    });
  }

  return centers;
};

const computeCategoricalClusters = (nodes, getKey) => {
  const keyToCluster = new Map();
  const clusterById = new Map();
  nodes.forEach((node) => {
    const key = getKey(node) || "Other";
    if (!keyToCluster.has(key)) keyToCluster.set(key, keyToCluster.size);
    clusterById.set(node.id, keyToCluster.get(key));
  });
  const clusterSizes = Array.from({ length: keyToCluster.size || 1 }, () => 0);
  clusterById.forEach((clusterId) => {
    clusterSizes[clusterId] += 1;
  });
  return { clusterById, clusterSizes };
};

const getTimeClusterKey = (node) => {
  if (!node.timestamp) return "No Date";
  const date = new Date(node.timestamp);
  if (Number.isNaN(date.getTime())) return "No Date";
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
};

// == Label sprite creation ==
function createLabelSprite(text, color) {
  const canvas = document.createElement("canvas");
  const fontSize = 24;
  const ctx = canvas.getContext("2d");
  const fontStr = `${fontSize}px sans-serif`;
  ctx.font = fontStr;
  const metrics = ctx.measureText(text);
  const w = Math.ceil(metrics.width) + 24;
  const h = fontSize + 14;
  canvas.width = w;
  canvas.height = h;
  const ctx2 = canvas.getContext("2d");
  ctx2.fillStyle = "#0f1926dd";
  const r = h / 2;
  ctx2.beginPath();
  ctx2.moveTo(r, 0);
  ctx2.lineTo(w - r, 0);
  ctx2.arc(w - r, r, r, -Math.PI / 2, Math.PI / 2);
  ctx2.lineTo(r, h);
  ctx2.arc(r, r, r, Math.PI / 2, -Math.PI / 2);
  ctx2.closePath();
  ctx2.fill();
  ctx2.strokeStyle = color + "88";
  ctx2.lineWidth = 1.5;
  ctx2.stroke();
  ctx2.fillStyle = "#e2e8f0";
  ctx2.font = fontStr;
  ctx2.textAlign = "center";
  ctx2.textBaseline = "middle";
  ctx2.fillText(text, w / 2, h / 2 + 1);
  const texture = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false });
  const sprite = new THREE.Sprite(mat);
  sprite.userData.isNodeLabel = true;
  sprite.scale.set(w / 8, h / 8, 1);
  return sprite;
}

// == BFS shortest path ==
function bfsShortestPath(nodes, links, startId, endId) {
  const adj = {};
  nodes.forEach((n) => { adj[n.id] = []; });
  links.forEach((l) => {
    const sid = getLinkSourceId(l);
    const tid = getLinkTargetId(l);
    if (adj[sid]) adj[sid].push({ neighbor: tid, link: l });
    if (adj[tid]) adj[tid].push({ neighbor: sid, link: l });
  });
  const visited = new Set([startId]);
  const queue = [[startId]];
  while (queue.length > 0) {
    const path = queue.shift();
    const current = path[path.length - 1];
    if (current === endId) return { nodeIds: new Set(path), path };
    for (const { neighbor } of (adj[current] || [])) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push([...path, neighbor]);
      }
    }
  }
  return null;
}

// == Graph analytics ==
function computeAnalytics(nodes, links) {
  const degree = {};
  const inDeg = {};
  const outDeg = {};
  nodes.forEach((n) => { degree[n.id] = 0; inDeg[n.id] = 0; outDeg[n.id] = 0; });
  links.forEach((l) => {
    const s = getLinkSourceId(l);
    const t = getLinkTargetId(l);
    degree[s] = (degree[s] || 0) + 1;
    degree[t] = (degree[t] || 0) + 1;
    outDeg[s] = (outDeg[s] || 0) + 1;
    inDeg[t] = (inDeg[t] || 0) + 1;
  });
  const hubs = [...nodes]
    .sort((a, b) => (degree[b.id] || 0) - (degree[a.id] || 0))
    .slice(0, 10)
    .map((n) => ({ ...n, deg: degree[n.id] || 0, inDeg: inDeg[n.id] || 0, outDeg: outDeg[n.id] || 0 }));
  const highImpact = [...nodes]
    .filter((n) => n.impact != null)
    .sort((a, b) => Math.abs(b.impact || 0) - Math.abs(a.impact || 0))
    .slice(0, 10);
  const edgeDist = {};
  links.forEach((l) => { edgeDist[l.edgeType] = (edgeDist[l.edgeType] || 0) + 1; });
  const parent = {};
  nodes.forEach((n) => { parent[n.id] = n.id; });
  const find = (x) => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
  const union = (a, b) => { parent[find(a)] = find(b); };
  links.forEach((l) => {
    const s = getLinkSourceId(l);
    const t = getLinkTargetId(l);
    union(s, t);
  });
  const clusters = {};
  nodes.forEach((n) => {
    const root = find(n.id);
    if (!clusters[root]) clusters[root] = [];
    clusters[root].push(n);
  });
  const clusterList = Object.values(clusters).sort((a, b) => b.length - a.length);
  const aiEdges = links.filter((l) => l.extractionMethod === "LLM").length;
  const ruleEdges = links.filter((l) => l.extractionMethod === "RULE").length;
  const confidences = links.filter((l) => l.confidence != null).map((l) => l.confidence);
  const avgConf = confidences.length > 0 ? confidences.reduce((a, b) => a + b, 0) / confidences.length : 0;
  return { hubs, highImpact, edgeDist, clusterList, aiEdges, ruleEdges, avgConf };
}

// == Main Component ==
export default function KnowledgeGraph3D({
  graphData,
  activeEdgeTypes,
  setActiveEdgeTypes,
  edges: rawEdges,
  onNodeClick,
  onNodeDoubleClick,
  loading,
  height = 700,
  layoutMode = "relationships",
  nodeSizeMode = "relationships",
}) {
  const fgRef = useRef();
  const containerRef = useRef();
  const lastAutoFitKeyRef = useRef("");
  const autoFitTimersRef = useRef([]);

  // Core state
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [showEdgeFilter, setShowEdgeFilter] = useState(false);
  const [showNodeTypeFilter, setShowNodeTypeFilter] = useState(false);
  const [activeNodeTypes, setActiveNodeTypes] = useState(new Set(Object.keys(EVENT_TYPE_LABELS)));
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  // Causal chain state
  const [causalChain, setCausalChain] = useState(null);
  const [causalLoading, setCausalLoading] = useState(false);
  const [causalPanelOpen, setCausalPanelOpen] = useState(false);
  const [causalDirection, setCausalDirection] = useState("both");
  const [causalDepth, setCausalDepth] = useState(3);

  // Path finder state
  const [pathFinderOpen, setPathFinderOpen] = useState(false);
  const [pathStart, setPathStart] = useState(null);
  const [pathEnd, setPathEnd] = useState(null);
  const [foundPath, setFoundPath] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);

  // Analytics panel
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  // Confidence filter
  const [minConfidence, setMinConfidence] = useState(0);

  // Timeline filter
  const [timelineEnabled, setTimelineEnabled] = useState(false);
  const [timeRange, setTimeRange] = useState([0, 100]);

  const closeAllPanels = useCallback(() => {
    setSearchOpen(false);
    setShowNodeTypeFilter(false);
    setShowEdgeFilter(false);
    setPathFinderOpen(false);
    setAnalyticsOpen(false);
  }, []);

  const clearAutoFitTimers = useCallback(() => {
    autoFitTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    autoFitTimersRef.current = [];
  }, []);

  // Edge type counts
  const edgeTypeCounts = useMemo(() => {
    const counts = {};
    (rawEdges || graphData?.edges || []).forEach((e) => {
      const t = e.data?.edgeType || "UNKNOWN";
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [rawEdges, graphData?.edges]);

  const toggleEdgeType = useCallback((type) => {
    if (!setActiveEdgeTypes) return;
    setActiveEdgeTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, [setActiveEdgeTypes]);

  // Measure container width (robust: sync + RAF + ResizeObserver)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => { const w = el.clientWidth; if (w > 0) setContainerWidth(w); };
    measure();
    const rafId = requestAnimationFrame(measure);
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => { cancelAnimationFrame(rafId); ro.disconnect(); };
  }, [loading]);

  // Compute timeline bounds
  const timelineBounds = useMemo(() => {
    if (!graphData?.nodes?.length) return { min: 0, max: 0 };
    const timestamps = graphData.nodes
      .filter((n) => n.type === "eventNode" && n.data.timestamp)
      .map((n) => new Date(n.data.timestamp).getTime())
      .filter((t) => !isNaN(t));
    if (timestamps.length === 0) return { min: 0, max: 0 };
    return { min: Math.min(...timestamps), max: Math.max(...timestamps) };
  }, [graphData?.nodes]);

  // Transform ReactFlow data to force-graph format
  const graphInput = useMemo(() => {
    if (!graphData?.nodes || !graphData?.edges) {
      return { nodes: [], links: [], _meta: { capped: false, totalCount: 0 } };
    }

    let tMin = 0, tMax = Infinity;
    if (timelineEnabled && timelineBounds.min < timelineBounds.max) {
      const range = timelineBounds.max - timelineBounds.min;
      tMin = timelineBounds.min + (timeRange[0] / 100) * range;
      tMax = timelineBounds.min + (timeRange[1] / 100) * range;
    }

    const eventNodes = graphData.nodes.filter((n) => {
      if (n.type !== "eventNode") return false;
      if (!activeNodeTypes.has(n.data.eventType) && EVENT_TYPE_LABELS[n.data.eventType]) return false;
      if (timelineEnabled && n.data.timestamp) {
        const t = new Date(n.data.timestamp).getTime();
        if (t < tMin || t > tMax) return false;
      }
      return true;
    });

    const totalCount = eventNodes.length;
    const nodeCount = eventNodes.length;
    const shouldPinLayout = nodeCount > 0;
    const baseNodes = eventNodes.map((n) => {
      const d = n.data;
      const degree = Number(d.degree || 0);
      return {
        id: String(n.id),
        eventType: d.eventType,
        eventTypeDisplay: d.eventTypeDisplay,
        title: d.title,
        fullTitle: d.fullTitle,
        description: d.description,
        degree: d.degree,
        color: d.color,
        currency: d.currency,
        currencies: d.currencies,
        impact: d.impact,
        timestamp: d.timestamp,
        metadata: d.metadata,
        sourceDegree: degree,
      };
    });

    const nodeIdSet = new Set(baseNodes.map((n) => n.id));

    const links = graphData.edges
      .filter((e) => {
        const edgeType = e.data?.edgeType;
        const src = getEndpointId(e.source);
        const tgt = getEndpointId(e.target);
        if (!activeEdgeTypes.has(edgeType) || !nodeIdSet.has(src) || !nodeIdSet.has(tgt)) return false;
        if (minConfidence > 0 && (e.data?.confidence || 0) < minConfidence) return false;
        return true;
      })
      .map((e) => {
        const source = getEndpointId(e.source);
        const target = getEndpointId(e.target);
        const edgeType = e.data?.edgeType;
        return {
          id: String(e.id || `${source}->${target}:${edgeType || "UNKNOWN"}`),
          source,
          target,
          sourceId: source,
          targetId: target,
          edgeType,
          edgeTypeDisplay: e.data?.edgeTypeDisplay,
          confidence: e.data?.confidence,
          description: e.data?.description,
          color: e.data?.color || EDGE_TYPE_COLORS[edgeType] || "#6b7280",
          extractionMethod: e.data?.extractionMethod,
        };
      });

    const visibleDegree = new Map(baseNodes.map((node) => [node.id, 0]));
    const incomingDegree = new Map(baseNodes.map((node) => [node.id, 0]));
    const outgoingDegree = new Map(baseNodes.map((node) => [node.id, 0]));
    links.forEach((link) => {
      const source = getLinkSourceId(link);
      const target = getLinkTargetId(link);
      visibleDegree.set(source, (visibleDegree.get(source) || 0) + 1);
      visibleDegree.set(target, (visibleDegree.get(target) || 0) + 1);
      outgoingDegree.set(source, (outgoingDegree.get(source) || 0) + 1);
      incomingDegree.set(target, (incomingDegree.get(target) || 0) + 1);
    });

    const graphNodes = baseNodes.map((node) => {
      const relationshipDegree = visibleDegree.get(node.id) || 0;
      const impact = Math.abs(Number(node.impact || 0));
      const sizingValue = nodeSizeMode === "uniform"
        ? 2
        : nodeSizeMode === "impact"
          ? Math.max(1.5, impact * 4)
          : relationshipDegree || Math.min(Number(node.sourceDegree || 0), 2);
      return {
        ...node,
        degree: relationshipDegree,
        visibleDegree: relationshipDegree,
        incomingDegree: incomingDegree.get(node.id) || 0,
        outgoingDegree: outgoingDegree.get(node.id) || 0,
        isIsolated: relationshipDegree === 0,
        val: nodeCount > DETAILED_RENDER_NODE_LIMIT
          ? Math.max(1.25, Math.min(8, 1.25 + Math.log2(sizingValue + 1)))
          : Math.max(2, Math.min(18, sizingValue + 1)),
      };
    });
    const linkedNodeCount = graphNodes.filter((node) => !node.isIsolated).length;
    const isolatedNodeCount = graphNodes.length - linkedNodeCount;
    const separateIsolated = layoutMode === "relationships";
    const clusteringNodes = separateIsolated && linkedNodeCount > 0
      ? graphNodes.filter((node) => !node.isIsolated)
      : graphNodes;

    const clusterResult = layoutMode === "event_type"
      ? computeCategoricalClusters(clusteringNodes, (node) => node.eventTypeDisplay || EVENT_TYPE_LABELS[node.eventType] || node.eventType)
      : layoutMode === "currency"
        ? computeCategoricalClusters(clusteringNodes, (node) => node.currencies?.[0] || node.currency || "Other")
        : layoutMode === "time"
          ? computeCategoricalClusters(clusteringNodes, getTimeClusterKey)
          : computeRelationshipClusters(clusteringNodes, links);
    const { clusterById, clusterSizes } = clusterResult;
    const clusterCount = Math.max(clusterSizes.length, 1);
    const maxClusterSize = Math.max(...clusterSizes, 1);
    const graphRadius = getGraphScale(nodeCount) * 0.54;
    const nodeSpacing = nodeCount > 10000
      ? 18
      : nodeCount > MASSIVE_GRAPH_NODE_LIMIT
        ? 20
        : nodeCount > SIMULATED_LAYOUT_NODE_LIMIT
          ? 24
          : nodeCount > DETAILED_RENDER_NODE_LIMIT
            ? 30
            : 36;
    const maxLocalRadius = clusterCount > 1
      ? Math.max(110, graphRadius / Math.max(1.25, Math.sqrt(clusterCount) * 0.46))
      : graphRadius * 0.92;
    const clusterCenters = computeClusterCenters(clusterCount, clusterSizes, links, clusterById, graphRadius);
    const localIndexes = new Map();

    let isolatedIndex = 0;
    const nodes = graphNodes.map((node) => {
      if (separateIsolated && node.isIsolated && linkedNodeCount > 0) {
        const index = isolatedIndex;
        isolatedIndex += 1;
        const phase = hashUnit(`isolated:${node.eventType}`) * Math.PI * 2;
        const shell = getSpherePoint(index, Math.max(isolatedNodeCount, 1), graphRadius * 1.18, phase);
        const jitter = 10 + hashUnit(`isolated:jitter:${node.id}`) * 20;
        const x = shell.x + (hashUnit(`isolated:x:${node.id}`) - 0.5) * jitter;
        const y = shell.y + (hashUnit(`isolated:y:${node.id}`) - 0.5) * jitter;
        const z = shell.z + (hashUnit(`isolated:z:${node.id}`) - 0.5) * jitter;
        const positionedNode = {
          ...node,
          clusterId: "unlinked",
          clusterSize: isolatedNodeCount,
          x,
          y,
          z,
        };

        if (shouldPinLayout) {
          positionedNode.fx = x;
          positionedNode.fy = y;
          positionedNode.fz = z;
        }

        return positionedNode;
      }

      const clusterId = clusterById.get(node.id) ?? 0;
      const clusterSize = clusterSizes[clusterId] || 1;
      const localIndex = localIndexes.get(clusterId) || 0;
      localIndexes.set(clusterId, localIndex + 1);
      const center = clusterCenters[clusterId] || { x: 0, y: 0, z: 0 };
      const densityRadius = nodeSpacing * Math.cbrt(Math.max(clusterSize, 1));
      const sizeShareRadius = maxLocalRadius * Math.max(0.42, Math.cbrt(clusterSize / maxClusterSize));
      const localRadius = Math.max(
        nodeCount > DETAILED_RENDER_NODE_LIMIT ? 48 : 22,
        Math.min(maxLocalRadius, Math.max(densityRadius, sizeShareRadius))
      );
      const local = getFilledSpherePoint(
        localIndex,
        clusterSize,
        localRadius,
        hashUnit(`local:${clusterId}`) * Math.PI * 2
      );
      const degree = Number(node.degree || 0);
      const hubPull = Math.min(Math.log2(degree + 1) * (nodeCount > DETAILED_RENDER_NODE_LIMIT ? 0.026 : 0.045), 0.34);
      const scale = 1 - hubPull;
      const x = center.x + local.x * scale;
      const y = center.y + local.y * scale;
      const z = center.z + local.z * scale;
      const positionedNode = {
        ...node,
        clusterId,
        clusterSize,
        x,
        y,
        z,
      };

      if (shouldPinLayout) {
        positionedNode.fx = x;
        positionedNode.fy = y;
        positionedNode.fz = z;
      }

      return positionedNode;
    });

    return {
      nodes,
      links,
      _meta: {
        capped: false,
        totalCount,
        pinned: shouldPinLayout,
        layout: layoutMode === "event_type"
          ? "event type groups"
          : layoutMode === "currency"
            ? "currency groups"
            : layoutMode === "time"
              ? "time groups"
              : "relationship communities",
        layoutMode,
        clusterCount,
        linkedNodeCount,
        isolatedNodeCount,
      },
    };
  }, [graphData, activeEdgeTypes, activeNodeTypes, minConfidence, timelineEnabled, timeRange, timelineBounds, layoutMode, nodeSizeMode]);

  const forceGraphData = useMemo(
    () => ({ nodes: graphInput.nodes, links: graphInput.links }),
    [graphInput]
  );

  // Analytics (memoized)
  const analytics = useMemo(
    () => computeAnalytics(graphInput.nodes, graphInput.links),
    [graphInput.nodes, graphInput.links]
  );

  // Highlight sets for causal chain, path finder, and selected node neighbourhood.
  const highlightNodeIds = useMemo(() => {
    const ids = new Set();
    if (selectedConnection?.nodeIds) selectedConnection.nodeIds.forEach((id) => ids.add(id));
    if (causalChain?.nodeIds) causalChain.nodeIds.forEach((id) => ids.add(id));
    if (foundPath?.nodeIds) foundPath.nodeIds.forEach((id) => ids.add(id));
    return ids;
  }, [causalChain, foundPath, selectedConnection]);

  const highlightLinkIds = useMemo(() => {
    const ids = new Set();
    if (selectedConnection?.linkIds) selectedConnection.linkIds.forEach((id) => ids.add(id));
    if (causalChain?.edges?.length) {
      const causalKeys = new Set();
      causalChain.edges.forEach((edge) => {
        causalKeys.add(`${edge.source_event_id}->${edge.target_event_id}:${edge.edge_type}`);
      });
      graphInput.links.forEach((link) => {
        const key = `${getLinkSourceId(link)}->${getLinkTargetId(link)}:${link.edgeType}`;
        if (causalKeys.has(key)) ids.add(link.id);
      });
    }
    if (foundPath?.path?.length > 1) {
      const pathPairs = new Set();
      for (let i = 0; i < foundPath.path.length - 1; i += 1) {
        const a = foundPath.path[i];
        const b = foundPath.path[i + 1];
        pathPairs.add(`${a}->${b}`);
        pathPairs.add(`${b}->${a}`);
      }
      graphInput.links.forEach((link) => {
        const pair = `${getLinkSourceId(link)}->${getLinkTargetId(link)}`;
        if (pathPairs.has(pair)) ids.add(link.id);
      });
    }
    return ids;
  }, [causalChain, foundPath, graphInput.links, selectedConnection]);

  const hasHighlight = highlightNodeIds.size > 0;
  const highlightNodeKey = useMemo(() => [...highlightNodeIds].sort().join("|"), [highlightNodeIds]);
  const highlightLinkKey = useMemo(() => [...highlightLinkIds].sort().join("|"), [highlightLinkIds]);
  const normalLinkStyle = useMemo(() => {
    return { opacity: 0.28, width: 0.22 };
  }, []);

  // Custom node rendering
  const graphNodeCount = graphInput.nodes.length;
  const isLargeGraph = graphNodeCount > DETAILED_RENDER_NODE_LIMIT;
  const isMassiveGraph = graphNodeCount > SIMULATED_LAYOUT_NODE_LIMIT;
  const enableHoverInteraction = graphNodeCount <= ARROW_RENDER_NODE_LIMIT;
  const hoveredNodeId = hoveredNode ? getNodeId(hoveredNode) : null;

  const nodeRenderAssets = useMemo(() => {
    const sphereGeometry = new THREE.SphereGeometry(
      1,
      isMassiveGraph ? 10 : isLargeGraph ? 12 : 18,
      isMassiveGraph ? 8 : isLargeGraph ? 10 : 14
    );
    const ringGeometry = new THREE.RingGeometry(1.35, 1.58, 36);
    const makeMaterial = (color, opacity, emissiveIntensity = 0.45) => new THREE.MeshStandardMaterial({
      color: new THREE.Color(color),
      emissive: new THREE.Color(color),
      emissiveIntensity,
      transparent: true,
      opacity,
      roughness: 0.48,
      metalness: 0.08,
    });
    const makeRingMaterial = (color, opacity) => new THREE.MeshBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const assets = {
      sphereGeometry,
      ringGeometry,
      base: {},
      isolated: {},
      highlight: {},
      root: {},
      ring: {},
      dim: makeMaterial("#253044", 0.3, 0.08),
    };
    Object.entries(EVENT_TYPE_COLORS).forEach(([type, color]) => {
      assets.base[type] = makeMaterial(color, isMassiveGraph ? 0.86 : 0.94, 0.46);
      assets.isolated[type] = makeMaterial(color, isMassiveGraph ? 0.38 : 0.48, 0.14);
      assets.highlight[type] = makeMaterial(color, 1, 0.9);
      assets.root[type] = makeMaterial(color, 1, 1.15);
      assets.ring[type] = makeRingMaterial(color, 0.62);
    });
    assets.base.UNKNOWN = makeMaterial("#64748b", isMassiveGraph ? 0.86 : 0.94, 0.35);
    assets.isolated.UNKNOWN = makeMaterial("#64748b", isMassiveGraph ? 0.38 : 0.48, 0.1);
    assets.highlight.UNKNOWN = makeMaterial("#94a3b8", 1, 0.8);
    assets.root.UNKNOWN = makeMaterial("#e2e8f0", 1, 0.85);
    assets.ring.UNKNOWN = makeRingMaterial("#94a3b8", 0.62);
    return assets;
  }, [isLargeGraph, isMassiveGraph]);

  const denseNodeThreeObject = useCallback((node) => {
    const nodeId = getNodeId(node);
    const eventType = node.eventType || "UNKNOWN";
    const baseColor = EVENT_TYPE_COLORS[eventType] || "#64748b";
    const isRoot = selectedConnection?.rootId === nodeId || causalChain?.rootId === nodeId;
    const isHighlighted = highlightNodeIds.has(nodeId);
    const isIsolated = node.isIsolated && !hasHighlight;
    const material = isRoot
      ? nodeRenderAssets.root[eventType] || nodeRenderAssets.root.UNKNOWN
      : isHighlighted
        ? nodeRenderAssets.highlight[eventType] || nodeRenderAssets.highlight.UNKNOWN
        : isIsolated
          ? nodeRenderAssets.isolated[eventType] || nodeRenderAssets.isolated.UNKNOWN
          : nodeRenderAssets.base[eventType] || nodeRenderAssets.base.UNKNOWN;
    const group = new THREE.Group();
    const sphere = new THREE.Mesh(nodeRenderAssets.sphereGeometry, material);
    const degree = Number(node.degree || 0);
    const impact = Math.abs(Number(node.impact || 0));
    const baseSize = isMassiveGraph ? 2.9 : isLargeGraph ? 3.9 : 5.8;
    const degreeBoost = nodeSizeMode === "relationships"
      ? Math.min(4.5, Math.log2(degree + 1)) * (isMassiveGraph ? 0.52 : isLargeGraph ? 0.66 : 0.95)
      : 0;
    const impactBoost = nodeSizeMode === "impact"
      ? Math.min(isLargeGraph ? 3.8 : 5.4, impact * (isLargeGraph ? 2.2 : 3.2))
      : nodeSizeMode === "relationships"
        ? Math.min(isLargeGraph ? 1.15 : 1.9, impact * (isLargeGraph ? 0.62 : 1.0))
        : 0;
    let size = nodeSizeMode === "uniform" ? baseSize : baseSize + degreeBoost + impactBoost;
    if (eventType === "PRICE_MOVE") size *= 1.08;
    if (isRoot) size *= 1.45;
    else if (isHighlighted) size *= 1.14;
    else if (isIsolated) size *= 0.52;
    sphere.scale.setScalar(size);
    group.add(sphere);

    if (isRoot || (!isMassiveGraph && hoveredNodeId === nodeId)) {
      const ringMaterial = nodeRenderAssets.ring[eventType] || nodeRenderAssets.ring.UNKNOWN;
      const ring = new THREE.Mesh(nodeRenderAssets.ringGeometry, ringMaterial);
      ring.scale.setScalar(size * 1.28);
      ring.lookAt(0, 1, 0);
      group.add(ring);
    }

    if (isRoot || (!isMassiveGraph && hoveredNodeId === nodeId)) {
      const label = node.title || node.fullTitle || "";
      if (label) {
        const labelSprite = createLabelSprite(
          label.length > 30 ? `${label.slice(0, 28)}...` : label,
          baseColor
        );
        labelSprite.position.set(size * 0.78, 0, 0);
        group.add(labelSprite);
      }
    }

    return group;
  }, [
    causalChain?.rootId,
    hasHighlight,
    highlightNodeIds,
    hoveredNodeId,
    isLargeGraph,
    isMassiveGraph,
    nodeRenderAssets,
    nodeSizeMode,
    selectedConnection?.rootId,
  ]);

  const nodeColor = useCallback((node) => {
    return EVENT_TYPE_COLORS[node.eventType] || "#6b7280";
  }, []);

  // Node hover
  const handleNodeHover = useCallback((node) => {
    setHoveredNode(node || null);
    if (containerRef.current) containerRef.current.style.cursor = node ? "pointer" : "default";
  }, []);

  // Node click
  const handleNodeClick = useCallback((node) => {
    clearAutoFitTimers();
    const clickedNodeId = getNodeId(node);
    if (onNodeClick && node) {
      onNodeClick(null, { type: "eventNode", data: { ...node, id: Number(clickedNodeId) } });
    }
    if (pathFinderOpen && node) {
      if (!pathStart) { setPathStart(node); return; }
      if (!pathEnd && clickedNodeId !== getNodeId(pathStart)) { setPathEnd(node); return; }
    }
    if (node) {
      const nodeIds = new Set([clickedNodeId]);
      const linkIds = new Set();
      let incomingCount = 0;
      let outgoingCount = 0;
      graphInput.links.forEach((link) => {
        const sid = getLinkSourceId(link);
        const tid = getLinkTargetId(link);
        if (sid !== clickedNodeId && tid !== clickedNodeId) return;
        nodeIds.add(sid);
        nodeIds.add(tid);
        linkIds.add(link.id);
        if (sid === clickedNodeId) outgoingCount += 1;
        if (tid === clickedNodeId) incomingCount += 1;
      });
      setSelectedConnection({
        rootId: clickedNodeId,
        nodeIds,
        linkIds,
        directConnectionCount: linkIds.size,
        incomingCount,
        outgoingCount,
        isIsolated: linkIds.size === 0,
      });
      setCausalChain(null);
      setCausalPanelOpen(false);
      setFoundPath(null);
    }
  }, [clearAutoFitTimers, onNodeClick, pathFinderOpen, pathStart, pathEnd, graphInput.links]);

  const handleNodeDrag = useCallback((node) => {
    if (!node) return;
    node.fx = node.x;
    node.fy = node.y;
    node.fz = node.z;
  }, []);

  const handleNodeDragEnd = useCallback((node) => {
    if (!node) return;
    node.fx = node.x;
    node.fy = node.y;
    node.fz = node.z;
  }, []);

  // Right-click: trace causal chain
  const handleNodeRightClick = useCallback(async (node) => {
    if (!node) return;
    const nodeId = getNodeId(node);
    if (onNodeDoubleClick) {
      onNodeDoubleClick(null, { type: "eventNode", data: { ...node, id: Number(nodeId) } });
    }
    setCausalLoading(true);
    setCausalPanelOpen(true);
    setSelectedConnection(null);
    try {
      const res = await getCausalChain(Number(nodeId), { direction: causalDirection, max_depth: causalDepth });
      const data = res.data;
      const chainNodeIds = new Set();
      chainNodeIds.add(nodeId);
      (data.chain_nodes || []).forEach((cn) => chainNodeIds.add(String(cn.event_id)));
      (data.chain || []).forEach((e) => {
        chainNodeIds.add(String(e.source_event_id));
        chainNodeIds.add(String(e.target_event_id));
      });
      setCausalChain({
        rootId: nodeId,
        nodeIds: chainNodeIds,
        edges: data.chain || [],
        events: data.events || {},
        chainNodes: data.chain_nodes || [],
        rootEvent: data.root_event,
        totalEdges: data.total_chain_edges,
      });
    } catch (err) {
      console.warn("Causal chain fetch failed:", err);
      setCausalChain(null);
    } finally {
      setCausalLoading(false);
    }
  }, [onNodeDoubleClick, causalDirection, causalDepth]);

  // Link hover
  const handleLinkHover = useCallback((link) => setHoveredLink(link || null), []);

  useEffect(() => {
    if (isMassiveGraph) setHoveredLink(null);
  }, [isMassiveGraph]);

  // Link color (with highlight support)
  const linkColor = useCallback((link) => {
    const sid = getLinkSourceId(link);
    const tid = getLinkTargetId(link);
    if (hasHighlight) return highlightLinkIds.has(link.id) ? "#f8fafc" : link.color || "#6b7280";
    if (!isMassiveGraph && hoveredNode) {
      const hoveredNodeId = getNodeId(hoveredNode);
      return (sid === hoveredNodeId || tid === hoveredNodeId) ? "#e2e8f0" : link.color || "#6b7280";
    }
    return link.color || "#6b7280";
  }, [hoveredNode, hasHighlight, highlightLinkIds, isMassiveGraph]);

  const linkVisibility = useCallback((link) => {
    if (hasHighlight) return highlightLinkIds.has(link.id);
    return true;
  }, [hasHighlight, highlightLinkIds]);

  // Link width (with highlight)
  const linkWidth = useCallback((link) => {
    const sid = getLinkSourceId(link);
    const tid = getLinkTargetId(link);
    if (hasHighlight) {
      return highlightLinkIds.has(link.id) ? 2.4 : normalLinkStyle.width;
    }
    if (!isMassiveGraph && hoveredNode) {
      const hoveredNodeId = getNodeId(hoveredNode);
      return (sid === hoveredNodeId || tid === hoveredNodeId) ? 1.4 : normalLinkStyle.width;
    }
    return normalLinkStyle.width * (0.75 + Math.max(0.2, Number(link.confidence || 0.5)) * 0.7);
  }, [hoveredNode, hasHighlight, highlightLinkIds, isMassiveGraph, normalLinkStyle.width]);

  useEffect(() => {
    try {
      fgRef.current?.refresh?.();
    } catch {
      return;
    }
  }, [highlightNodeKey, highlightLinkKey]);

  // Scene setup
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const t = setTimeout(() => {
      try {
        const renderer = fg.renderer();
        if (renderer) {
          renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isMassiveGraph ? 0.85 : isLargeGraph ? 1 : 1.5));
          renderer.sortObjects = false;
        }
        const controls = fg.controls();
        if (controls) {
          controls.enableDamping = true;
          controls.dampingFactor = 0.06;
          controls.rotateSpeed = 0.55;
          controls.zoomSpeed = 0.75;
          controls.panSpeed = 0.75;
          controls.screenSpacePanning = true;
        }
        const scene = fg.scene();
        if (!scene || scene.userData.__lightsAdded) return;
        scene.add(new THREE.AmbientLight(0x8899aa, 2.5));
        const d1 = new THREE.DirectionalLight(0xffffff, 1.2);
        d1.position.set(200, 300, 200);
        scene.add(d1);
        const d2 = new THREE.DirectionalLight(0x6688cc, 0.6);
        d2.position.set(-200, -100, -200);
        scene.add(d2);
        const starCount = 600;
        const starGeo = new THREE.BufferGeometry();
        const starPos = new Float32Array(starCount * 3);
        for (let i = 0; i < starCount * 3; i++) starPos[i] = (hashUnit(`star:${i}`) - 0.5) * 2000;
        starGeo.setAttribute("position", new THREE.BufferAttribute(starPos, 3));
        scene.add(new THREE.Points(starGeo, new THREE.PointsMaterial({
          color: 0x4466aa, size: 0.8, transparent: true, opacity: 0.4, sizeAttenuation: true,
        })));
        scene.background = new THREE.Color(0x0a0e17);
        scene.fog = new THREE.FogExp2(0x0a0e17, 0.0008);
        scene.userData.__lightsAdded = true;
      } catch {
        return;
      }
    }, 50);
    return () => clearTimeout(t);
  }, [graphInput.nodes.length, containerWidth, isLargeGraph, isMassiveGraph]);

  const autoFitKey = useMemo(() => {
    const meta = graphInput._meta || {};
    const dataMeta = graphData?.meta || {};
    return [
      graphInput.nodes.length,
      graphInput.links.length,
      meta.layout,
      meta.clusterCount,
      dataMeta.requested_limit,
      dataMeta.currency_filter,
      dataMeta.symbol_filter,
      dataMeta.event_type_filter,
      dataMeta.days_back,
    ].join("|");
  }, [graphData?.meta, graphInput._meta, graphInput.links.length, graphInput.nodes.length]);

  // Fit on new graph/layout loads only. Do not refit when node selection opens
  // the detail panel and changes the canvas width.
  useEffect(() => {
    if (!fgRef.current || graphInput.nodes.length === 0 || containerWidth <= 0) return undefined;
    if (lastAutoFitKeyRef.current === autoFitKey) return undefined;
    lastAutoFitKeyRef.current = autoFitKey;
    clearAutoFitTimers();
    const padding = isMassiveGraph ? 120 : 60;
    const t1 = setTimeout(() => { try { fgRef.current.zoomToFit(500, padding); } catch { return; } }, isMassiveGraph ? 250 : 800);
    const t2 = setTimeout(() => { try { fgRef.current.zoomToFit(700, padding); } catch { return; } }, isMassiveGraph ? 1300 : 2500);
    autoFitTimersRef.current = [t1, t2];
    return clearAutoFitTimers;
  }, [autoFitKey, clearAutoFitTimers, containerWidth, graphInput.nodes.length, isMassiveGraph]);

  // Force config
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    try {
      fg.d3Force("charge")?.strength(isMassiveGraph ? -10 : isLargeGraph ? -70 : -250).distanceMax(isMassiveGraph ? 140 : isLargeGraph ? 360 : 600);
      fg.d3Force("link")?.distance(isMassiveGraph ? 10 : isLargeGraph ? 28 : 80).strength(isMassiveGraph ? 0.02 : isLargeGraph ? 0.08 : 0.18);
      fg.d3Force("center")?.strength(isMassiveGraph ? 0.005 : isLargeGraph ? 0.035 : 0.03);
    } catch {
      return;
    }
  }, [graphInput, isLargeGraph, isMassiveGraph]);

  // Search
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const q = searchQuery.toLowerCase();
    setSearchResults(
      graphInput.nodes.filter((n) => {
        const title = (n.title || n.fullTitle || "").toLowerCase();
        const desc = (n.description || "").toLowerCase();
        const evtType = (n.eventTypeDisplay || n.eventType || "").toLowerCase();
        const currency = (n.currencies || []).join(" ").toLowerCase();
        const symbol = (n.symbols || []).join(" ").toLowerCase();
        return title.includes(q) || desc.includes(q) || evtType.includes(q) || currency.includes(q) || symbol.includes(q);
      }).slice(0, 15)
    );
  }, [searchQuery, graphInput.nodes]);

  const flyToNode = useCallback((node) => {
    if (!fgRef.current || !node) return;
    const distance = 100;
    const distRatio = 1 + distance / Math.hypot(node.x || 1, node.y || 1, node.z || 1);
    fgRef.current.cameraPosition(
      { x: node.x * distRatio, y: node.y * distRatio, z: node.z * distRatio },
      { x: node.x, y: node.y, z: node.z },
      1200
    );
    setHoveredNode(node);
    setSearchOpen(false);
    setSearchQuery("");
  }, []);

  const toggleNodeType = useCallback((type) => {
    setActiveNodeTypes((prev) => { const next = new Set(prev); next.has(type) ? next.delete(type) : next.add(type); return next; });
  }, []);

  const nodeTypeCounts = useMemo(() => {
    const counts = {};
    (graphData?.nodes || []).forEach((n) => {
      if (n.type !== "eventNode") return;
      counts[n.data?.eventType || "UNKNOWN"] = (counts[n.data?.eventType || "UNKNOWN"] || 0) + 1;
    });
    return counts;
  }, [graphData?.nodes]);

  const hasViewFilters = useMemo(() => (
    activeNodeTypes.size !== Object.keys(EVENT_TYPE_LABELS).length
    || activeEdgeTypes.size !== Object.keys(EDGE_TYPE_LABELS).length
    || minConfidence > 0
    || timelineEnabled
  ), [activeEdgeTypes, activeNodeTypes, minConfidence, timelineEnabled]);

  const resetViewFilters = useCallback(() => {
    setActiveNodeTypes(new Set(Object.keys(EVENT_TYPE_LABELS)));
    setMinConfidence(0);
    setTimelineEnabled(false);
    setTimeRange([0, 100]);
    if (setActiveEdgeTypes) setActiveEdgeTypes(new Set(Object.keys(EDGE_TYPE_LABELS)));
  }, [setActiveEdgeTypes]);

  // Path finder execution
  useEffect(() => {
    if (pathStart && pathEnd) {
      const result = bfsShortestPath(graphInput.nodes, graphInput.links, getNodeId(pathStart), getNodeId(pathEnd));
      setFoundPath(result);
    } else {
      setFoundPath(null);
    }
  }, [pathStart, pathEnd, graphInput.nodes, graphInput.links]);

  // Distance-based label visibility
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg || graphInput.nodes.length === 0) return;
    let animId;
    const updateLabels = () => {
      try {
        const camera = fg.camera();
        if (camera) {
          const camPos = camera.position;
          graphInput.nodes.forEach((node) => {
            if (!node.__threeObj) return;
            const group = node.__threeObj;
            for (let i = 0; i < group.children.length; i++) {
              const child = group.children[i];
              if (child.isSprite && child.userData?.isNodeLabel) {
                const dx = camPos.x - (node.x || 0), dy = camPos.y - (node.y || 0), dz = camPos.z - (node.z || 0);
                const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
                child.visible = dist < 350;
                if (child.visible) child.material.opacity = Math.max(0.3, Math.min(1, (350 - dist) / 200));
              }
            }
          });
        }
      } catch {
        return;
      }
      animId = requestAnimationFrame(updateLabels);
    };
    const t = setTimeout(() => { animId = requestAnimationFrame(updateLabels); }, 3000);
    return () => { clearTimeout(t); cancelAnimationFrame(animId); };
  }, [graphInput.nodes, hoveredNodeId, selectedConnection?.rootId, causalChain?.rootId]);

  // Clear highlights helper
  const clearHighlights = useCallback(() => {
    setSelectedConnection(null);
    setCausalChain(null);
    setCausalPanelOpen(false);
    setFoundPath(null);
    setPathStart(null);
    setPathEnd(null);
  }, []);

  // Format date
  const fmtDate = (ts) => {
    return formatStoredDate(ts, { month: "short", day: "numeric", year: "numeric" }, "");
  };

  // Loading state
  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center bg-[#0a0e17] rounded-lg" style={{ width: "100%", height }}>
        <div className="text-center space-y-3">
          <div className="relative w-16 h-16 mx-auto">
            <div className="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping" />
            <div className="absolute inset-2 rounded-full border-2 border-primary/50 animate-pulse" />
            <div className="absolute inset-4 rounded-full bg-primary/20 animate-pulse" />
          </div>
          <p className="text-sm text-textSecondary">Loading 3D graph...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (graphInput.nodes.length === 0) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center text-textSecondary gap-3 bg-[#0a0e17] rounded-lg" style={{ width: "100%", height }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="opacity-40">
          <circle cx="12" cy="12" r="3" /><line x1="12" y1="1" x2="12" y2="4" /><line x1="12" y1="20" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="6.34" y2="6.34" /><line x1="17.66" y1="17.66" x2="19.78" y2="19.78" />
        </svg>
        <p className="text-sm">No graph data. Adjust filters and click "Load Graph".</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: "100%", height }} className="relative bg-[#0a0e17] rounded-lg overflow-hidden">
      {containerWidth > 0 && (
        <ForceGraph3D
          ref={fgRef}
          graphData={forceGraphData}
          width={containerWidth}
          height={height}
          backgroundColor="#0a0e17"
          nodeThreeObject={denseNodeThreeObject}
          nodeThreeObjectExtend={false}
          nodeColor={nodeColor}
          nodeVal={(node) => node.val}
          nodeRelSize={isMassiveGraph ? 1.55 : isLargeGraph ? 2.3 : 4}
          nodeResolution={isMassiveGraph ? 4 : isLargeGraph ? 6 : 16}
          nodeOpacity={isMassiveGraph ? 0.82 : 0.9}
          onNodeClick={handleNodeClick}
          onNodeRightClick={handleNodeRightClick}
          onNodeHover={enableHoverInteraction ? handleNodeHover : undefined}
          onNodeDrag={handleNodeDrag}
          onNodeDragEnd={handleNodeDragEnd}
          linkColor={linkColor}
          linkVisibility={linkVisibility}
          linkWidth={linkWidth}
          linkOpacity={normalLinkStyle.opacity}
          linkResolution={2}
          linkDirectionalParticles={0}
          linkDirectionalParticleWidth={0}
          linkDirectionalParticleSpeed={0}
          linkDirectionalParticleColor={(link) => link.color || "#ffffff"}
          linkDirectionalArrowLength={3.5}
          linkDirectionalArrowRelPos={0.88}
          linkDirectionalArrowResolution={5}
          linkDirectionalArrowColor={(link) => link.color || "#6b7280"}
          linkCurvature={0.035}
          onLinkHover={enableHoverInteraction && !isMassiveGraph ? handleLinkHover : undefined}
          linkHoverPrecision={enableHoverInteraction && !isMassiveGraph ? 4 : 0}
          d3AlphaDecay={0.18}
          d3VelocityDecay={0.72}
          warmupTicks={0}
          cooldownTicks={1}
          cooldownTime={1200}
          enableNodeDrag={true}
          enableNavigationControls={true}
          enablePointerInteraction={true}
          showNavInfo={false}
        />
      )}

      {/* TOP-LEFT: Stats */}
      <div className="absolute top-3 left-3 bg-[#0f1926]/90 rounded-lg border border-[#1e293b] px-3 py-2 text-xs text-[#94a3b8] backdrop-blur-sm pointer-events-none z-10">
        <span className="text-[#e2e8f0] font-semibold">{graphInput.nodes.length}</span> nodes{" "}
        <span className="text-[#e2e8f0] font-semibold">{graphInput.links.length}</span> edges
        {minConfidence > 0 && <span className="ml-2 text-[#f59e0b]">{`conf\u2265${(minConfidence * 100).toFixed(0)}%`}</span>}
        {graphInput._meta.layout && (
          <span className="ml-2 text-[#10b981]">
            {`${graphInput._meta.clusterCount || 0} ${graphInput._meta.layout}`}
          </span>
        )}
        {graphInput._meta.isolatedNodeCount > 0 && (
          <span className="ml-2 text-[#94a3b8]">
            {`${graphInput._meta.linkedNodeCount || 0} linked / ${graphInput._meta.isolatedNodeCount || 0} unlinked events`}
          </span>
        )}
        {graphData?.meta?.truncated && (
          <span className="ml-2 text-[#f59e0b]">
            {`limited ${graphData.meta.total_events?.toLocaleString?.() || graphData.meta.total_events} / ${graphData.meta.scoped_events?.toLocaleString?.() || graphData.meta.scoped_events}`}
          </span>
        )}
        {graphData?.meta?.currency_filter && (
          <span className="ml-2 text-[#3b82f6] font-medium">{graphData.meta.currency_filter}</span>
        )}
        {graphData?.meta?.symbol_filter && (
          <span className="ml-2 text-[#f59e0b] font-medium">{graphData.meta.symbol_filter}</span>
        )}
        <span className="ml-2 opacity-60">{`Click\u2192connections | Right-click\u2192causal chain`}</span>
      </div>

      {/* TOP-RIGHT: Toolbar */}
      <div className="absolute top-3 right-3 z-20 flex gap-1.5 flex-wrap justify-end">
        <ToolbarBtn active={searchOpen} onClick={() => { closeAllPanels(); setSearchOpen((v) => !v); setSearchQuery(""); }}>
          Search
        </ToolbarBtn>
        <ToolbarBtn active={pathFinderOpen} onClick={() => { closeAllPanels(); clearHighlights(); setPathFinderOpen((v) => !v); }}>
          Path Finder
        </ToolbarBtn>
        <ToolbarBtn active={analyticsOpen} onClick={() => { closeAllPanels(); setAnalyticsOpen((v) => !v); }}>
          Analytics
        </ToolbarBtn>
        <ToolbarBtn active={showNodeTypeFilter} onClick={() => { closeAllPanels(); setShowNodeTypeFilter((v) => !v); }}>
          {`Node Types \u25BE`}
        </ToolbarBtn>
        <ToolbarBtn active={showEdgeFilter} onClick={() => { closeAllPanels(); setShowEdgeFilter((v) => !v); }}>
          {`Edges \u25BE`}
        </ToolbarBtn>
        {hasViewFilters && (
          <ToolbarBtn onClick={resetViewFilters}>
            Reset Filters
          </ToolbarBtn>
        )}
      </div>

      {/* Clear highlight banner */}
      {hasHighlight && (
        <div className="absolute top-12 left-3 z-20">
          <button onClick={clearHighlights}
            className="bg-[#f59e0b]/20 hover:bg-[#f59e0b]/30 border border-[#f59e0b]/40 text-[#f59e0b] text-[11px] px-3 py-1.5 rounded-lg backdrop-blur-sm transition-colors flex items-center gap-1.5">
            <span className="text-xs">{"\u2715"}</span>
            {causalChain
              ? `Causal chain: ${highlightNodeIds.size} nodes`
              : foundPath
                ? `Path: ${foundPath.path?.length || 0} hops`
                : selectedConnection?.isIsolated
                  ? "Selected node: no stored direct relationships"
                  : `Selected node: ${selectedConnection?.directConnectionCount || 0} direct links (${selectedConnection?.outgoingCount || 0} out, ${selectedConnection?.incomingCount || 0} in)`}
            <span className="opacity-70">{"\u2014"} click to clear</span>
          </button>
        </div>
      )}

      {/* Search Panel */}
      {searchOpen && (
        <div className="absolute top-12 right-3 z-20 w-80 bg-[#0f1926]/95 rounded-lg border border-[#1e293b] shadow-2xl backdrop-blur-sm p-3">
          <input autoFocus type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search events, currencies, types..."
            className="w-full bg-[#1e293b]/60 text-[#e2e8f0] text-xs rounded px-2.5 py-2 border border-[#334155] focus:border-[#3b82f6] focus:outline-none placeholder-[#64748b]" />
          {searchResults.length > 0 && (
            <div className="mt-2 max-h-60 overflow-y-auto space-y-0.5">
              {searchResults.map((n) => (
                <button key={n.id} onClick={() => flyToNode(n)}
                  className="flex items-center gap-2 w-full px-2 py-1.5 rounded text-left text-[11px] hover:bg-[#1e293b]/80 transition-colors">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: EVENT_TYPE_COLORS[n.eventType] || "#6b7280" }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[#e2e8f0] truncate">{stripHtml(n.title || n.fullTitle)}</p>
                    <div className="flex items-center gap-2 text-[9px] text-[#64748b]">
                      <span>{n.eventTypeDisplay || n.eventType}</span>
                      {n.impact != null && (
                        <span className={n.impact > 0 ? "text-emerald-400" : n.impact < 0 ? "text-red-400" : ""}>
                          {n.impact > 0 ? "+" : ""}{n.impact?.toFixed(2)}
                        </span>
                      )}
                      <span>{fmtDate(n.timestamp)}</span>
                    </div>
                  </div>
                  <span className="text-[9px] text-[#475569] shrink-0">{n.degree || 0} conn</span>
                </button>
              ))}
            </div>
          )}
          {searchQuery && searchResults.length === 0 && (
            <p className="text-[10px] text-[#64748b] mt-2 px-1">No matching events found</p>
          )}
        </div>
      )}

      {/* Path Finder Panel */}
      {pathFinderOpen && (
        <div className="absolute top-12 right-3 z-20 w-80 bg-[#0f1926]/95 rounded-lg border border-[#1e293b] shadow-2xl backdrop-blur-sm p-3">
          <p className="text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wide mb-2">Shortest Path Finder</p>
          <p className="text-[10px] text-[#64748b] mb-3">Click two nodes in the graph to find the shortest causal path between them.</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#64748b] w-10">From:</span>
              {pathStart ? (
                <div className="flex-1 flex items-center gap-1.5 bg-[#1e293b]/50 rounded px-2 py-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: EVENT_TYPE_COLORS[pathStart.eventType] }} />
                  <span className="text-[10px] text-[#e2e8f0] truncate">{pathStart.title}</span>
                  <button onClick={() => { setPathStart(null); setFoundPath(null); }} className="text-[#ef4444] text-xs ml-auto">{"\u2715"}</button>
                </div>
              ) : (
                <span className="text-[10px] text-[#475569] italic">Click a node...</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#64748b] w-10">To:</span>
              {pathEnd ? (
                <div className="flex-1 flex items-center gap-1.5 bg-[#1e293b]/50 rounded px-2 py-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: EVENT_TYPE_COLORS[pathEnd.eventType] }} />
                  <span className="text-[10px] text-[#e2e8f0] truncate">{pathEnd.title}</span>
                  <button onClick={() => { setPathEnd(null); setFoundPath(null); }} className="text-[#ef4444] text-xs ml-auto">{"\u2715"}</button>
                </div>
              ) : (
                <span className="text-[10px] text-[#475569] italic">{pathStart ? "Click another node..." : "Select start first"}</span>
              )}
            </div>
          </div>
          {foundPath && (
            <div className="mt-3 pt-3 border-t border-[#1e293b]">
              <p className="text-[11px] text-emerald-400 font-medium mb-2">
                Path found: {foundPath.path.length - 1} hop{foundPath.path.length - 1 !== 1 ? "s" : ""}
              </p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {foundPath.path.map((nodeId, i) => {
                  const node = graphInput.nodes.find((n) => n.id === nodeId);
                  return node ? (
                    <button key={nodeId} onClick={() => flyToNode(node)}
                      className="flex items-center gap-1.5 w-full px-2 py-1 rounded text-left hover:bg-[#1e293b]/60 transition-colors">
                      <span className="text-[9px] text-[#475569] w-3">{i + 1}</span>
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: EVENT_TYPE_COLORS[node.eventType] }} />
                      <span className="text-[10px] text-[#e2e8f0] truncate flex-1">{node.title}</span>
                      {i < foundPath.path.length - 1 && <span className="text-[9px] text-[#475569]">{"\u2192"}</span>}
                    </button>
                  ) : null;
                })}
              </div>
            </div>
          )}
          {pathStart && pathEnd && !foundPath && (
            <p className="mt-3 text-[10px] text-[#ef4444]">No path found between these nodes.</p>
          )}
          <button onClick={() => { setPathStart(null); setPathEnd(null); setFoundPath(null); }}
            className="mt-2 w-full text-[10px] text-[#64748b] hover:text-[#94a3b8] transition-colors py-1">
            Reset Selection
          </button>
        </div>
      )}

      {/* Analytics Panel */}
      {analyticsOpen && (
        <div className="absolute top-12 right-3 z-20 w-96 max-h-[70vh] overflow-y-auto bg-[#0f1926]/95 rounded-lg border border-[#1e293b] shadow-2xl backdrop-blur-sm p-3">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wide">Graph Analytics</p>
            <button onClick={() => setAnalyticsOpen(false)}
              className="text-[#64748b] hover:text-[#e2e8f0] text-xs transition-colors">{"\u2715"}</button>
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <StatCard label="Clusters" value={analytics.clusterList.length} />
            <StatCard label="Avg Conf" value={`${(analytics.avgConf * 100).toFixed(0)}%`} />
            <StatCard label="AI Edges" value={analytics.aiEdges} sub={`/ ${analytics.aiEdges + analytics.ruleEdges}`} />
          </div>

          {/* Edge type distribution */}
          <div className="mb-3">
            <p className="text-[10px] text-[#64748b] font-semibold mb-1.5">Edge Type Distribution</p>
            <div className="space-y-1">
              {Object.entries(analytics.edgeDist).sort(([, a], [, b]) => b - a).map(([type, count]) => {
                const pct = graphInput.links.length > 0 ? (count / graphInput.links.length) * 100 : 0;
                return (
                  <div key={type} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: EDGE_TYPE_COLORS[type] || "#6b7280" }} />
                    <span className="text-[10px] text-[#94a3b8] w-20">{EDGE_TYPE_LABELS[type] || type}</span>
                    <div className="flex-1 h-1.5 bg-[#1e293b] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: EDGE_TYPE_COLORS[type] || "#6b7280" }} />
                    </div>
                    <span className="text-[9px] text-[#64748b] w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Hub nodes */}
          <div className="mb-3">
            <p className="text-[10px] text-[#64748b] font-semibold mb-1.5">Hub Nodes (Most Connected)</p>
            <div className="space-y-0.5 max-h-48 overflow-y-auto">
              {analytics.hubs.map((n) => (
                <button key={n.id} onClick={() => flyToNode(n)}
                  className="flex items-center gap-2 w-full px-2 py-1 rounded text-left hover:bg-[#1e293b]/60 transition-colors">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: EVENT_TYPE_COLORS[n.eventType] }} />
                  <span className="text-[10px] text-[#e2e8f0] truncate flex-1">{n.title}</span>
                  <span className="text-[9px] text-[#64748b]">{n.deg} conn</span>
                  <span className="text-[8px] text-[#475569]">{`${n.inDeg}\u2193 ${n.outDeg}\u2191`}</span>
                </button>
              ))}
            </div>
          </div>

          {/* High impact nodes */}
          <div className="mb-3">
            <p className="text-[10px] text-[#64748b] font-semibold mb-1.5">Highest Impact Events</p>
            <div className="space-y-0.5 max-h-48 overflow-y-auto">
              {analytics.highImpact.map((n) => (
                <button key={n.id} onClick={() => flyToNode(n)}
                  className="flex items-center gap-2 w-full px-2 py-1 rounded text-left hover:bg-[#1e293b]/60 transition-colors">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: EVENT_TYPE_COLORS[n.eventType] }} />
                  <span className="text-[10px] text-[#e2e8f0] truncate flex-1">{n.title}</span>
                  <span className={`text-[10px] font-bold ${(n.impact || 0) > 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {n.impact > 0 ? "+" : ""}{n.impact?.toFixed(2)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Clusters */}
          <div>
            <p className="text-[10px] text-[#64748b] font-semibold mb-1.5">
              Connected Components ({analytics.clusterList.length})
            </p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {analytics.clusterList.slice(0, 10).map((cluster, i) => {
                const types = {};
                cluster.forEach((n) => { types[n.eventType] = (types[n.eventType] || 0) + 1; });
                return (
                  <div key={i} className="bg-[#1e293b]/30 rounded px-2 py-1.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-[#e2e8f0] font-medium">Cluster {i + 1}</span>
                      <span className="text-[9px] text-[#64748b]">{cluster.length} nodes</span>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {Object.entries(types).sort(([, a], [, b]) => b - a).map(([type, count]) => (
                        <span key={type} className="text-[8px] px-1 py-0.5 rounded"
                          style={{ backgroundColor: (EVENT_TYPE_COLORS[type] || "#6b7280") + "20", color: EVENT_TYPE_COLORS[type] || "#6b7280" }}>
                          {EVENT_TYPE_LABELS[type] || type} ({count})
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Node Type Filter */}
      {showNodeTypeFilter && (
        <div className="absolute top-12 right-3 z-20 bg-[#0f1926]/95 rounded-lg border border-[#1e293b] shadow-2xl backdrop-blur-sm p-2.5 w-52">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wide">Node Types</span>
            <button onClick={() => {
              const all = Object.keys(EVENT_TYPE_LABELS);
              setActiveNodeTypes(new Set(all.every((t) => activeNodeTypes.has(t)) ? [] : all));
            }} className="text-[10px] text-[#60a5fa] hover:text-[#93bbfc]">
              {Object.keys(EVENT_TYPE_LABELS).every((t) => activeNodeTypes.has(t)) ? "Hide all" : "Show all"}
            </button>
          </div>
          <div className="space-y-1">
            {Object.entries(EVENT_TYPE_LABELS).map(([type, label]) => {
              const count = nodeTypeCounts[type] || 0;
              const active = activeNodeTypes.has(type);
              return (
                <button key={type} onClick={() => toggleNodeType(type)}
                  className={`flex items-center w-full gap-2 px-2 py-1 rounded text-left text-[11px] transition-all hover:bg-[#1e293b]/80 ${
                    active ? "bg-[#1e293b]/60 text-[#e2e8f0]" : "text-[#64748b] opacity-50"
                  }`}>
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: active ? EVENT_TYPE_COLORS[type] : "#4b5563" }} />
                  <span className={`flex-1 ${!active ? "line-through" : ""}`}>{label}</span>
                  <span className="text-[9px] opacity-60">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Edge Type Filter */}
      {showEdgeFilter && (
        <div className="absolute top-12 right-3 z-20 bg-[#0f1926]/95 rounded-lg border border-[#1e293b] shadow-2xl backdrop-blur-sm p-2.5 w-56">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold text-[#94a3b8] uppercase tracking-wide">Edge Types</span>
            <button onClick={() => {
              if (!setActiveEdgeTypes) return;
              const all = Object.keys(EDGE_TYPE_LABELS);
              setActiveEdgeTypes(new Set(all.every((t) => activeEdgeTypes.has(t)) ? [] : all));
            }} className="text-[10px] text-[#60a5fa] hover:text-[#93bbfc]">
              {Object.keys(EDGE_TYPE_LABELS).every((t) => activeEdgeTypes.has(t)) ? "Hide all" : "Show all"}
            </button>
          </div>
          <div className="space-y-1 mb-3">
            {Object.entries(EDGE_TYPE_LABELS).map(([type, label]) => {
              const count = edgeTypeCounts[type] || 0;
              const active = activeEdgeTypes.has(type);
              return (
                <button key={type} onClick={() => toggleEdgeType(type)}
                  className={`flex items-center w-full gap-2 px-2 py-1 rounded text-left text-[11px] transition-all hover:bg-[#1e293b]/80 ${
                    active ? "bg-[#1e293b]/60 text-[#e2e8f0]" : "text-[#64748b] opacity-50"
                  }`}>
                  <div className="w-3.5 h-1 rounded-full" style={{ backgroundColor: active ? EDGE_TYPE_COLORS[type] : "#4b5563" }} />
                  <span className={`flex-1 ${!active ? "line-through" : ""}`}>{label}</span>
                  <span className="text-[9px] opacity-60">{count}</span>
                </button>
              );
            })}
          </div>
          {/* Confidence slider */}
          <div className="border-t border-[#1e293b] pt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-[#64748b]">Min Confidence</span>
              <span className="text-[10px] text-[#e2e8f0] font-medium">{(minConfidence * 100).toFixed(0)}%</span>
            </div>
            <input type="range" min="0" max="100" value={minConfidence * 100}
              onChange={(e) => setMinConfidence(Number(e.target.value) / 100)}
              className="w-full h-1 bg-[#1e293b] rounded-lg appearance-none cursor-pointer accent-[#3b82f6]" />
          </div>
        </div>
      )}

      {/* Causal Chain Panel (LEFT SIDE) */}
      {causalPanelOpen && (
        <div className="absolute top-14 left-3 z-20 w-80 max-h-[65vh] overflow-y-auto bg-[#0f1926]/95 rounded-lg border border-[#1e293b] shadow-2xl backdrop-blur-sm">
          <div className="sticky top-0 bg-[#0f1926] p-3 border-b border-[#1e293b]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#f59e0b] animate-pulse" />
                <span className="text-[11px] font-semibold text-[#e2e8f0] uppercase tracking-wide">Causal Chain</span>
              </div>
              <button onClick={() => { setCausalPanelOpen(false); setCausalChain(null); }}
                className="text-[#64748b] hover:text-[#e2e8f0] text-xs transition-colors">{"\u2715"}</button>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <select value={causalDirection} onChange={(e) => setCausalDirection(e.target.value)}
                className="bg-[#1e293b] text-[10px] text-[#e2e8f0] border border-[#334155] rounded px-1.5 py-1 focus:outline-none">
                <option value="both">Both Directions</option>
                <option value="backward">Backward (Causes)</option>
                <option value="forward">Forward (Effects)</option>
              </select>
              <select value={causalDepth} onChange={(e) => setCausalDepth(Number(e.target.value))}
                className="bg-[#1e293b] text-[10px] text-[#e2e8f0] border border-[#334155] rounded px-1.5 py-1 focus:outline-none">
                {[1, 2, 3, 4, 5].map((d) => <option key={d} value={d}>Depth {d}</option>)}
              </select>
            </div>
            {causalChain && (
              <p className="text-[10px] text-[#64748b]">
                {`${causalChain.nodeIds.size} events \u00B7 ${causalChain.totalEdges} edges traced`}
              </p>
            )}
          </div>

          {causalLoading && (
            <div className="p-6 text-center">
              <div className="w-6 h-6 border-2 border-[#3b82f6]/30 border-t-[#3b82f6] rounded-full animate-spin mx-auto mb-2" />
              <p className="text-[10px] text-[#64748b]">Tracing causal chain...</p>
            </div>
          )}

          {causalChain && !causalLoading && (
            <div className="p-3">
              {/* Root event */}
              {causalChain.rootEvent && (
                <div className="mb-3 p-2 bg-[#f59e0b]/10 border border-[#f59e0b]/20 rounded-lg">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: EVENT_TYPE_COLORS[causalChain.rootEvent.event_type] }} />
                    <span className="text-[9px] font-semibold uppercase text-[#f59e0b]">Root Event</span>
                  </div>
                  <p className="text-[11px] text-[#e2e8f0] font-medium leading-tight">{causalChain.rootEvent.title}</p>
                  <p className="text-[9px] text-[#64748b] mt-1">{fmtDate(causalChain.rootEvent.timestamp)}</p>
                </div>
              )}

              {/* Chain steps grouped by depth */}
              {(() => {
                const byDepth = {};
                causalChain.chainNodes.forEach((cn) => {
                  if (cn.depth === 0) return;
                  if (!byDepth[cn.depth]) byDepth[cn.depth] = [];
                  byDepth[cn.depth].push(cn);
                });
                return Object.entries(byDepth).sort(([a], [b]) => a - b).map(([depth, nodes]) => (
                  <div key={depth} className="mb-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className="w-4 h-px bg-[#334155]" />
                      <span className="text-[9px] text-[#475569] font-semibold uppercase">Depth {depth}</span>
                      <div className="flex-1 h-px bg-[#334155]" />
                    </div>
                    <div className="space-y-1 ml-2 border-l border-[#1e293b] pl-2">
                      {nodes.map((cn) => {
                        const graphNode = graphInput.nodes.find((n) => n.id === String(cn.event_id));
                        return (
                          <button key={cn.event_id}
                            onClick={() => graphNode && flyToNode(graphNode)}
                            className="flex items-start gap-2 w-full p-1.5 rounded text-left hover:bg-[#1e293b]/60 transition-colors">
                            <div className="w-2 h-2 rounded-full mt-0.5 shrink-0"
                              style={{ backgroundColor: EVENT_TYPE_COLORS[cn.event_type] || "#6b7280" }} />
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] text-[#e2e8f0] leading-tight">{cn.title}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {cn.edge_type && (
                                  <span className="text-[8px] px-1 py-0.5 rounded"
                                    style={{ backgroundColor: (EDGE_TYPE_COLORS[cn.edge_type] || "#6b7280") + "20", color: EDGE_TYPE_COLORS[cn.edge_type] || "#6b7280" }}>
                                    {cn.edge_type}
                                  </span>
                                )}
                                {cn.edge_confidence != null && (
                                  <span className="text-[8px] text-[#475569]">{(cn.edge_confidence * 100).toFixed(0)}% conf</span>
                                )}
                                {cn.impact_magnitude != null && (
                                  <span className={`text-[8px] font-bold ${cn.impact_magnitude > 0 ? "text-emerald-400" : cn.impact_magnitude < 0 ? "text-red-400" : "text-[#64748b]"}`}>
                                    {cn.impact_magnitude > 0 ? "+" : ""}{cn.impact_magnitude.toFixed(2)}
                                  </span>
                                )}
                              </div>
                              <p className="text-[8px] text-[#475569] mt-0.5">{fmtDate(cn.timestamp)}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}

              {/* Chain edges summary */}
              {causalChain.edges.length > 0 && (
                <div className="mt-3 pt-2 border-t border-[#1e293b]">
                  <p className="text-[9px] text-[#475569] font-semibold uppercase mb-1">Edge Details</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {causalChain.edges.map((e, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[9px]">
                        <div className="w-2 h-0.5 rounded" style={{ backgroundColor: EDGE_TYPE_COLORS[e.edge_type] || "#6b7280" }} />
                        <span className="text-[#94a3b8]">{e.edge_type}</span>
                        <span className="text-[#475569]">conf:{(e.confidence * 100).toFixed(0)}%</span>
                        {e.extraction_method === "LLM" && (
                          <span className="text-[8px] font-bold px-1 rounded bg-violet-500/20 text-violet-400">AI</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Re-trace button */}
              <button onClick={() => {
                const rootNode = graphInput.nodes.find((n) => n.id === causalChain.rootId);
                if (rootNode) handleNodeRightClick(rootNode);
              }} className="mt-3 w-full bg-[#3b82f6]/20 hover:bg-[#3b82f6]/30 border border-[#3b82f6]/30 text-[#60a5fa] text-[10px] py-1.5 rounded transition-colors">
                Re-trace with current settings
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hovered node tooltip */}
      {hoveredNode && !searchOpen && !pathFinderOpen && !analyticsOpen && (
        <div className="absolute top-14 right-3 w-72 bg-[#0f1926]/95 rounded-lg border border-[#1e293b] shadow-2xl backdrop-blur-sm pointer-events-none z-10">
          <div className="p-3 space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: EVENT_TYPE_COLORS[hoveredNode.eventType] || "#6b7280" }} />
              <span className="text-[10px] font-semibold uppercase text-[#94a3b8]">
                {hoveredNode.eventTypeDisplay || hoveredNode.eventType}
              </span>
            </div>
            <p className="text-sm font-medium text-[#e2e8f0] leading-tight">
              {stripHtml(hoveredNode.fullTitle || hoveredNode.title)}
            </p>
            <div className="flex items-center justify-between text-[10px] text-[#64748b]">
              <span>{fmtDate(hoveredNode.timestamp)}</span>
              <span>{hoveredNode.degree || 0} connections</span>
            </div>
            {hoveredNode.currencies?.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {hoveredNode.currencies.map((c) => (
                  <span key={c} className="text-[10px] bg-[#3b82f6]/20 text-[#60a5fa] px-1.5 py-0.5 rounded">{c}</span>
                ))}
              </div>
            )}
            {hoveredNode.impact != null && (
              <div className="flex items-center gap-1 text-xs">
                <span className="text-[#64748b]">Impact:</span>
                <span className={`font-bold ${hoveredNode.impact > 0.05 ? "text-emerald-400" : hoveredNode.impact < -0.05 ? "text-red-400" : "text-[#94a3b8]"}`}>
                  {hoveredNode.impact > 0 ? "+" : ""}{hoveredNode.impact?.toFixed(2)}
                </span>
              </div>
            )}
            {hoveredNode.description && (
              <p className="text-[11px] text-[#64748b] leading-relaxed line-clamp-3">{stripHtml(hoveredNode.description)}</p>
            )}
            <p className="text-[9px] text-[#475569] italic">Right-click to trace causal chain</p>
          </div>
        </div>
      )}

      {/* Hovered link tooltip */}
      {hoveredLink && (
        <div className="absolute bottom-14 left-3 bg-[#0f1926]/95 rounded-lg border border-[#1e293b] shadow-2xl backdrop-blur-sm px-3 py-2 pointer-events-none z-10">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-4 h-1 rounded" style={{ backgroundColor: hoveredLink.color || "#6b7280" }} />
            <span className="text-xs font-semibold text-[#e2e8f0]">{hoveredLink.edgeTypeDisplay || hoveredLink.edgeType}</span>
            {hoveredLink.extractionMethod === "LLM" && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 border border-violet-500/30">AI</span>
            )}
          </div>
          {hoveredLink.confidence != null && (
            <p className="text-[11px] text-[#64748b]">Confidence: <span className="text-[#e2e8f0]">{(hoveredLink.confidence * 100).toFixed(0)}%</span></p>
          )}
          {hoveredLink.description && (
            <p className="text-[11px] text-[#64748b] max-w-[250px] line-clamp-2">{hoveredLink.description}</p>
          )}
        </div>
      )}

      {/* Timeline Slider (BOTTOM) */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        <button onClick={() => setTimelineEnabled((v) => !v)}
          className={`bg-[#0f1926]/90 rounded-lg border px-2 py-1 text-[10px] backdrop-blur-sm transition-colors ${
            timelineEnabled ? "border-[#3b82f6] text-[#60a5fa]" : "border-[#1e293b] text-[#64748b] hover:text-[#94a3b8]"
          }`}>
          Timeline
        </button>
        {timelineEnabled && timelineBounds.min < timelineBounds.max && (
          <div className="bg-[#0f1926]/90 rounded-lg border border-[#1e293b] px-3 py-1.5 backdrop-blur-sm flex items-center gap-3">
            <span className="text-[9px] text-[#64748b]">
              {fmtDate(timelineBounds.min + (timeRange[0] / 100) * (timelineBounds.max - timelineBounds.min))}
            </span>
            <div className="relative w-48">
              <input type="range" min="0" max="100" value={timeRange[0]}
                onChange={(e) => setTimeRange([Math.min(Number(e.target.value), timeRange[1] - 1), timeRange[1]])}
                className="absolute w-full h-1 bg-transparent appearance-none cursor-pointer z-10 accent-[#3b82f6]" />
              <input type="range" min="0" max="100" value={timeRange[1]}
                onChange={(e) => setTimeRange([timeRange[0], Math.max(Number(e.target.value), timeRange[0] + 1)])}
                className="absolute w-full h-1 bg-[#1e293b] rounded appearance-none cursor-pointer accent-[#3b82f6]" />
            </div>
            <span className="text-[9px] text-[#64748b]">
              {fmtDate(timelineBounds.min + (timeRange[1] / 100) * (timelineBounds.max - timelineBounds.min))}
            </span>
          </div>
        )}
      </div>

      {/* Controls (BOTTOM-RIGHT) */}
      <div className="absolute bottom-3 right-3 flex gap-2 z-10">
        <button onClick={() => { try { fgRef.current?.zoomToFit(600, 60); } catch { return; } }}
          className="bg-[#0f1926]/90 hover:bg-[#1e293b] rounded-lg border border-[#1e293b] px-2.5 py-1.5 text-[11px] text-[#94a3b8] hover:text-[#e2e8f0] transition-colors backdrop-blur-sm">
          Fit View
        </button>
        <button onClick={() => { try { fgRef.current?.cameraPosition({ x: 0, y: 0, z: 400 }, { x: 0, y: 0, z: 0 }, 1000); } catch { return; } }}
          className="bg-[#0f1926]/90 hover:bg-[#1e293b] rounded-lg border border-[#1e293b] px-2.5 py-1.5 text-[11px] text-[#94a3b8] hover:text-[#e2e8f0] transition-colors backdrop-blur-sm">
          Reset Camera
        </button>
      </div>
    </div>
  );
}

// Small reusable components
function ToolbarBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      className={`bg-[#0f1926]/90 hover:bg-[#1e293b] rounded-lg border px-2.5 py-1.5 text-[11px] transition-colors backdrop-blur-sm ${
        active ? "border-[#3b82f6] text-[#60a5fa]" : "border-[#1e293b] text-[#94a3b8] hover:text-[#e2e8f0]"
      }`}>
      {children}
    </button>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-[#1e293b]/40 rounded-lg p-2 text-center">
      <p className="text-[14px] font-bold text-[#e2e8f0]">{value}{sub && <span className="text-[10px] text-[#64748b] font-normal">{sub}</span>}</p>
      <p className="text-[9px] text-[#64748b] uppercase">{label}</p>
    </div>
  );
}
